import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import type { Scrapper, ScrapperPayload } from '../services/scrappersService';
import type { ProductOpt } from '../services/itemsService';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ErrorIcon from '@mui/icons-material/Error';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import CloudSyncIcon from '@mui/icons-material/CloudSync';

const STATUS_LABELS: Record<string, string> = { w: 'Waiting', a: 'Active', i: 'Inactive', '': '—' };
const STATUS_STYLES: Record<string, string> = {
  w: 'bg-warning/10 text-warning',
  a: 'bg-success/10 text-success',
  i: 'bg-surface-high text-on-surface-variant',
  '': 'bg-surface-high text-on-surface-variant',
};

const RETAILERS = [
  'MDComputers', 'Amazon', 'Flipkart', 'Vedant', 'PrimeABGB',
  'TPS Tech', 'National PC', 'SMC', 'OnlySSD', 'Elitehubs', 'PC Studio', 'Vishal Peripherals',
];

const stripWww = (s: string) => s.replace(/^www\./i, '');

const Scrappers: React.FC = () => {
  const api = useApi();
  const { user } = useAuth();
  const isAdmin = user?.isAdmin ?? false;

  const [scrappers, setScrappers] = useState<Scrapper[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [products, setProducts] = useState<ProductOpt[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [productSearch, setProductSearch] = useState('');
  const [productTitles, setProductTitles] = useState<Record<number, string>>({});
  const searchSeqRef = useRef(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  // A product search is in flight — the dropdown shows a hint instead of the
  // previous (stale) result set.
  const [searchingProducts, setSearchingProducts] = useState(false);
  // The last product search failed — the message is shown in the dropdown.
  const [productSearchError, setProductSearchError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Partial<Scrapper> | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [competitors, setCompetitors] = useState<string[]>([]);
  const [competitorsApiFailed, setCompetitorsApiFailed] = useState(false);

  const showToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  };

  const load = useCallback(async () => {
    if (!selectedProductId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page) });
      params.set('prod_id', String(selectedProductId));
      if (filterStatus) params.set('status', filterStatus);
      if (search) params.set('search', search);
      const res = await api.scrappers.list(params);
      setScrappers(res.scrappers || []);
      setTotal(res.total || 0);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [api, page, selectedProductId, filterStatus, search]);

  const loadProducts = useCallback(async (search = '') => {
    setLoadingProducts(true);
    try {
      const res = await api.items.listOptions(100, search);
      setProducts(res.items || []);
      setProductSearchError(null);
    } catch (err) {
      // A failed product search must not leave the previous list on screen —
      // it would look like the search found those products.
      setProducts([]);
      setProductSearchError((err as Error).message || 'Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  }, [api]);

  useEffect(() => {
    loadProducts('');
    // Load competitors for the modal Company dropdown (domain-style titles).
    // The Competitors API is stable; a failure just falls back to RETAILERS.
    api.comparison.competitors()
      .then((res) => setCompetitors(res.competitors || []))
      .catch(() => setCompetitorsApiFailed(true));
  }, [loadProducts, api]);

  // Debounced server-side product search. Each keystroke bumps the sequence
  // token so stale, out-of-order responses never overwrite newer ones.
  // Clearing the search box leaves the last fetched slice in place.
  // The API matches product_id, prod_title, category, brand and sku — the
  // response IS the result set, so the dropdown must not re-filter it here
  // (a title-only filter used to hide products found by their ID).
  useEffect(() => {
    const seq = ++searchSeqRef.current;
    if (!productSearch.trim()) {
      // Empty-search guard — the last fetched slice stays.
      setSearchingProducts(false);
      setProductSearchError(null);
      return;
    }
    // The list on screen belongs to the PREVIOUS query — mark the search as
    // running so the dropdown does not show those stale products while the
    // new result is on its way.
    setSearchingProducts(true);
    setProductSearchError(null);
    const timer = setTimeout(async () => {
      if (seq !== searchSeqRef.current) return; // stale — discard
      try {
        const res = await api.items.listOptions(100, productSearch);
        if (seq !== searchSeqRef.current) return; // double-stale guard
        setProducts(res.items || []);
        setProductSearchError(null);
      } catch (err) {
        if (seq !== searchSeqRef.current) return;
        // A failed search is not an empty result — say so.
        setProducts([]);
        setProductSearchError((err as Error).message || 'Search failed');
      } finally {
        if (seq === searchSeqRef.current) setSearchingProducts(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch, api]);
  useEffect(() => { setSelected(new Set()); setPage(1); setProductSearch(''); }, [selectedProductId]);
  useEffect(() => { if (selectedProductId) load(); }, [load, selectedProductId]);

  const openCreate = () => {
    setEditing({ prod_id: selectedProductId!, title: '', url: '', status: 'w', selector: '', color: '#000000', stock_selector: '' });
    setShowModal(true);
  };

  const openEdit = (s: Scrapper) => {
    setEditing({ ...s });
    setShowModal(true);
  };

  const check = async () => {
    if (!editing) return;
    try {
      const payload: ScrapperPayload = {
        prod_id: editing.prod_id!,
        title: editing.title || '',
        url: editing.url || '',
        status: editing.status || 'w',
      };
      if (isAdmin) {
        payload.selector = editing.selector || '';
        payload.color = editing.color || '#000000';
        payload.stock_selector = editing.stock_selector || '';
      }
      if (editing.id) {
        await api.scrappers.update(editing.id, payload);
      } else {
        await api.scrappers.create(payload);
      }
      setShowModal(false);
      setEditing(null);
      showToast('Scrapper saved!');
      await load();
    } catch (err) { setError((err as Error).message); }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this scrapper?')) return;
    try {
      await api.scrappers.remove(id);
      showToast('Scrapper deleted');
      await load();
    } catch (err) { setError((err as Error).message); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} scrapper(s)?`)) return;
    try {
      await api.scrappers.bulkDelete(Array.from(selected));
      setSelected(new Set());
      showToast('Scrappers deleted');
      await load();
    } catch (err) { setError((err as Error).message); }
  };

  const toggleSelect = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const totalPages = Math.max(1, Math.ceil(total / 20));

  const selectedProduct = products.find((p) => p.product_id === selectedProductId);

  // The products list may not contain the selected product once a search
  // narrows the options, so fall back to the title cached at selection time.
  const displayTitle = selectedProduct?.prod_title
    ?? (selectedProductId !== null ? productTitles[selectedProductId] : undefined)
    ?? '— Choose a product —';

  // Competitors API excludes MDComputers by design (isMdTitle filter), but
  // products DO have MD URLs — append it manually (guarded against duplicates).
  // On API failure, fall back to the legacy display-name list.
  const companyOptions = competitorsApiFailed
    ? RETAILERS
    : competitors.includes('mdcomputers.in')
      ? competitors
      : [...competitors, 'mdcomputers.in'];

  return (
    <div className="space-y-4 px-4 mt-4">
      {/* Toast — portaled to <body> and rendered top-right at all viewports.
          Class string simplified: the previous `bottom-4` + `sm:top-4` cascade stretched the fixed element
          from top to bottom of the viewport, producing a full-page-height strip. Top-right only now. */}
      {toastMsg && typeof document !== 'undefined' && createPortal(
        <div className="fixed top-4 right-4 z-50 bg-sidebar-bg text-white px-4 py-3 rounded-xl shadow-xl border border-primary-fixed-dim/30 flex items-center gap-2 animate-toast-in">
          <CheckIcon className="text-success text-[16px] sm:text-[20px]" />
          <span className="text-xs font-medium">{toastMsg}</span>
        </div>,
        document.body
      )}

      {/* Product Selector— always visible */}
      <div className="bg-surface border border-border-subtle rounded-xl p-3 sm:p-5">
        <label className="text-[10px] font-label-caps text-on-surface-variant block mb-2">Select Product</label>
        <div className="relative w-full">
          {/* Trigger */}
          <button
            type="button"
            onClick={() => setIsDropdownOpen((v) => !v)}
            disabled={loadingProducts}
            className="w-full flex items-center justify-between bg-white border border-border-subtle rounded-lg px-3 py-2.5 text-left focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <span className={selectedProduct ? 'text-body-sm text-on-surface' : 'text-body-sm text-on-surface-variant'}>
              {displayTitle}
            </span>
            <ExpandMoreIcon className="text-on-surface-variant text-[16px]" />
          </button>

          {/* Dropdown panel */}
          {isDropdownOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setIsDropdownOpen(false)} />
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-border-subtle rounded-xl shadow-xl z-50 overflow-hidden">
                {/* Search */}
                <div className="p-2 border-b border-border-subtle">
                  <div className="relative">
                    <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 text-on-surface-variant text-[14px] pointer-events-none" />
                    <input
                      type="text"
                      autoFocus
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      placeholder="Search products..."
                      className="w-full pl-8 pr-3 py-2 bg-surface border border-border-subtle rounded-lg text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                </div>
                {/* Options */}
                <div className="max-h-60 overflow-y-auto">
                  {searchingProducts && (
                    <p className="px-3 py-4 text-body-sm text-on-surface-variant text-center">Searching…</p>
                  )}
                  {!searchingProducts && productSearchError && (
                    <p className="px-3 py-4 text-body-sm text-danger text-center">
                      Search failed — {productSearchError}
                    </p>
                  )}
                  {!searchingProducts && !productSearchError && products.map((p) => (
                    <button
                      key={p.product_id}
                      type="button"
                      onClick={() => {
                        setProductTitles((prev) => ({ ...prev, [p.product_id]: p.prod_title }));
                        setSelectedProductId(p.product_id);
                        setProductSearch('');
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-body-sm hover:bg-surface-low transition-colors ${selectedProductId === p.product_id ? 'bg-primary/5 text-primary font-medium' : 'text-on-surface'}`}
                    >
                      {p.prod_title}
                    </button>
                  ))}
                  {!searchingProducts && !productSearchError && products.length === 0 && (
                    <p className="px-3 py-4 text-body-sm text-on-surface-variant text-center">No products found</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        {selectedProductId && selectedProduct && (
          <p className="text-body-sm text-on-surface-variant mt-2">
            Product ID <span className="font-mono font-medium text-on-surface">#{selectedProductId}</span>
            <span className="ml-2">— {selectedProduct.prod_title}</span>
          </p>
        )}
      </div>

      {/* No product selected — empty state */}
      {!selectedProductId && (
        <div className="bg-surface border border-border-subtle rounded-xl">
          <div className="flex flex-col items-center justify-center py-20 px-4">
            <div className="w-16 h-16 rounded-full bg-surface-container flex items-center justify-center mb-4">
              <ShoppingCartIcon className="text-on-surface-variant text-[32px]" />
            </div>
            <p className="font-headline-sm text-on-surface font-semibold mb-1">Select a product</p>
            <p className="text-body-sm text-on-surface-variant text-center max-w-xs">Choose a product above to view and manage its tracked retailer URLs.</p>
          </div>
        </div>
      )}

      {/* Product selected — show scrappers */}
      {selectedProductId && (
        <>
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <h1 className="font-headline-md text-on-surface font-semibold">{selectedProduct?.prod_title ?? `Product #${selectedProductId}`}</h1>
              <p className="text-body-xs sm:text-body-sm text-on-surface-variant mt-0.5">{total} tracked URL{total !== 1 ? 's' : ''}</p>
            </div>
            <div className="flex gap-2">
              {selected.size > 0 && (
                <button
                  onClick={bulkDelete}
                  className="bg-danger/10 text-danger px-3 sm:px-4 py-2 rounded-lg text-body-xs sm:text-body-sm font-medium hover:bg-danger/20 transition-all flex items-center gap-1.5 sm:gap-2"
                >
                  <DeleteIcon className="text-[14px] sm:text-[16px]" />
                  <span className="hidden sm:inline">Delete </span>{selected.size}
                </button>
              )}
              <button
                onClick={openCreate}
                className="bg-primary-container text-on-primary px-3 sm:px-4 py-2 rounded-lg text-body-xs sm:text-body-sm font-semibold hover:bg-primary transition-all flex items-center gap-1.5 sm:gap-2 shadow-sm"
              >
                <AddIcon className="text-[14px] sm:text-[16px]" />
                Add URL
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative min-w-0 sm:min-w-[130px]">
              <select
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                className="appearance-none w-full bg-surface border border-border-subtle rounded-lg px-3 py-2 pr-8 text-body-xs sm:text-body-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer"
              >
                <option value="">Any Status</option>
                <option value="w">Waiting</option>
                <option value="a">Active</option>
                <option value="i">Inactive</option>
              </select>
              <ExpandMoreIcon className="absolute right-2 top-1/2 -translate-y-1/2 text-on-surface-variant text-[16px] pointer-events-none" />
            </div>
            <div className="flex-1 min-w-0">
              <input
                type="search"
                placeholder="Search title or URL..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="w-full bg-surface border border-border-subtle rounded-lg px-3 py-2 text-body-xs sm:text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>

          {/* Table */}
          <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden shadow-2xs min-w-0">
            {error && (
              <div className="p-3 sm:p-4 bg-danger/10 border-b border-danger/20 text-danger text-body-xs sm:text-body-sm flex items-center gap-2">
                <ErrorIcon className="text-[14px] sm:text-[16px]" />
                {error}
              </div>
            )}

            <div className="overflow-x-auto min-w-0">
              <table className="w-full">
                <thead>
                  <tr className="bg-surface-low border-b border-border-subtle">
                    <th className="w-9 px-1.5 sm:px-4 py-2.5">
                      <input
                        type="checkbox"
                        checked={selected.size === scrappers.length && scrappers.length > 0}
                        onChange={(e) => setSelected(e.target.checked ? new Set(scrappers.map((s) => s.id)) : new Set())}
                        className="rounded border-border-subtle"
                      />
                    </th>
                    <th className="text-left text-[10px] font-label-caps text-on-surface-variant uppercase px-1.5 sm:px-4 py-2.5 hidden sm:table-cell">ID</th>
                    <th className="text-left text-[10px] font-label-caps text-on-surface-variant uppercase px-1.5 sm:px-4 py-2.5">Company</th>
                    <th className="text-left text-[10px] font-label-caps text-on-surface-variant uppercase px-1.5 sm:px-4 py-2.5 hidden lg:table-cell">URL</th>
                    <th className="text-left text-[10px] font-label-caps text-on-surface-variant uppercase px-1.5 sm:px-4 py-2.5">Status</th>
                    <th className="text-right text-[10px] font-label-caps text-on-surface-variant uppercase px-1.5 sm:px-4 py-2.5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="text-center py-12"><div className="flex justify-center"><div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" /></div></td></tr>
                  ) : scrappers.length === 0 ? (
                    <tr><td colSpan={6}><div className="flex flex-col items-center justify-center py-12 sm:py-16"><div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-surface-container-high flex items-center justify-center mb-3"><CloudSyncIcon className="text-on-surface-variant text-[20px] sm:text-[24px]" /></div><p className="text-on-surface-variant font-body-xs sm:text-body-sm">No URLs tracked for this product</p><p className="text-on-surface-variant/60 text-xs mt-1 hidden sm:block">Click "Add URL" to start tracking a retailer.</p></div></td></tr>
                  ) : scrappers.map((s) => (
                    <tr key={s.id} className="border-b border-border-subtle/50 hover:bg-surface-low transition-colors">
                      <td className="px-1.5 sm:px-4 py-2">
                        <input
                          type="checkbox"
                          checked={selected.has(s.id)}
                          onChange={() => toggleSelect(s.id)}
                          className="rounded border-border-subtle"
                        />
                      </td>
                      <td className="px-1.5 sm:px-4 py-2 font-data-mono text-xs text-on-surface hidden sm:table-cell">{s.id}</td>
                      <td className="px-1.5 sm:px-4 py-2 font-body-xs sm:text-body-sm text-on-surface max-w-[120px] truncate">{stripWww(s.title || '')}</td>
                      <td className="px-1.5 sm:px-4 py-2 max-w-[120px] sm:max-w-[240px] truncate hidden lg:table-cell">
                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-body-xs sm:text-body-sm" title={s.url}>
                          {s.url.replace(/^https?:\/\//, '')}
                        </a>
                      </td>
                      <td className="px-1.5 sm:px-4 py-2">
                        <span className={`text-[10px] font-label-caps px-1.5 sm:px-2 py-0.5 rounded-full ${STATUS_STYLES[s.status] || STATUS_STYLES['']}`}>
                          {STATUS_LABELS[s.status] || s.status}
                        </span>
                      </td>
                      <td className="px-1.5 sm:px-4 py-2">
                        <div className="flex justify-end gap-0.5 sm:gap-1">
                          <button onClick={() => openEdit(s)} className="p-1 sm:p-1.5 rounded hover:bg-surface-high transition-colors" title="Edit">
                            <EditIcon className="text-[12px] sm:text-[16px] text-primary" />
                          </button>
                          <button onClick={() => remove(s.id)} className="p-1 sm:p-1.5 rounded hover:bg-surface-high transition-colors" title="Delete">
                            <DeleteIcon className="text-[12px] sm:text-[16px] text-danger" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-3 sm:px-4 py-3 border-t border-border-subtle">
                <span className="text-body-xs sm:text-body-sm text-on-surface-variant">Page {page}/{totalPages} ({total} total)</span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page <= 1}
                    className="w-8 h-8 flex items-center justify-center border border-border-subtle rounded-lg hover:bg-surface-low disabled:opacity-40 text-on-surface transition-colors"
                  >
                    <ArrowBackIcon className="text-[14px] sm:text-[16px]" />
                  </button>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page >= totalPages}
                    className="w-8 h-8 flex items-center justify-center border border-border-subtle rounded-lg hover:bg-surface-low disabled:opacity-40 text-on-surface transition-colors"
                  >
                    <ArrowForwardIcon className="text-[14px] sm:text-[16px]" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal — portaled to <body> to escape the space-y-4 wrapper,
          which would otherwise inject a 16px margin-top on the fixed layer. */}
      {showModal && editing && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowModal(false)}>
          <div className="bg-surface rounded-xl shadow-xl w-full max-w-lg border border-border-subtle animate-toast-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-border-subtle">
              <h3 className="font-headline-sm text-on-surface">{editing.id ? 'Edit URL' : 'Add URL'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 rounded hover:bg-surface-high transition-colors">
                <CloseIcon className="text-on-surface-variant text-[20px]" />
              </button>
            </div>
            <div className="p-4 sm:p-5 space-y-3 sm:space-y-4 max-h-[60vh] overflow-y-auto">
              {editing.id && (
                <div className="bg-surface-low rounded-lg px-3 py-2 text-body-xs sm:text-body-sm text-on-surface-variant">
                  Product: <span className="font-medium text-on-surface">{selectedProduct?.prod_title ?? `#${selectedProductId}`}</span>
                </div>
              )}
              <div>
                <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1">Company</label>
                {editing.title && !companyOptions.includes(editing.title) ? (
                  <input
                    type="text"
                    value={editing.title || ''}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    placeholder="Company name"
                    className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2 text-body-xs sm:text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                ) : (
                  <div className="relative">
                    <select
                      value={editing.title || ''}
                      onChange={(e) => setEditing({ ...editing, title: e.target.value === '__other__' ? '' : e.target.value })}
                      className="appearance-none w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2 pr-8 text-body-xs sm:text-body-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer"
                    >
                      <option value="">Select...</option>
                      {companyOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <ExpandMoreIcon className="absolute right-2.5 top-1/2 -translate-y-1/2 text-on-surface-variant text-[16px] pointer-events-none" />
                  </div>
                )}
              </div>

              <div>
                <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1">URL</label>
                <input
                  type="text"
                  value={editing.url || ''}
                  onChange={(e) => setEditing({ ...editing, url: e.target.value })}
                  placeholder="https://..."
                  className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2 text-body-xs sm:text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1">Status</label>
                <select
                  value={editing.status || 'w'}
                  onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                  className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2 text-body-xs sm:text-body-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer"
                >
                  <option value="w">Waiting</option>
                  <option value="a">Active</option>
                  <option value="i">Inactive</option>
                </select>
              </div>

              {isAdmin && (
                <>
                  <div>
                    <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1">CSS Selector</label>
                    <input
                      type="text"
                      value={editing.selector || ''}
                      onChange={(e) => setEditing({ ...editing, selector: e.target.value })}
                      placeholder=".product-price"
                      className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2 text-body-xs sm:text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/20 font-mono"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:gap-4">
                    <div>
                      <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1">Color</label>
                      <input
                        type="color"
                        value={editing.color || '#000000'}
                        onChange={(e) => setEditing({ ...editing, color: e.target.value })}
                        className="w-full h-9 rounded-lg cursor-pointer"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1">Stock CSS Selector</label>
                      <input
                        type="text"
                        value={editing.stock_selector || ''}
                        onChange={(e) => setEditing({ ...editing, stock_selector: e.target.value })}
                        placeholder=".stock-indicator"
                        className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2 text-body-xs sm:text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/20 font-mono"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="px-4 sm:px-5 py-3 sm:py-4 border-t border-border-subtle flex gap-2 sm:gap-3">
              <button onClick={() => setShowModal(false)} className="flex-1 bg-surface border border-border-subtle py-2 rounded-lg text-body-xs sm:text-body-sm font-medium text-on-surface hover:bg-surface-high transition-colors">
                Cancel
              </button>
              <button
                onClick={check}
                disabled={!editing.title || !editing.url}
                className="flex-1 bg-primary-container text-on-primary py-2 rounded-lg text-body-xs sm:text-body-sm font-semibold hover:bg-primary transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5 sm:gap-2"
              >
                <SaveIcon className="text-[14px] sm:text-[16px]" />
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Scrappers;
